import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../services/api";
import { emitFinanceEvent } from "./finance/financeDispatcher";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./LabPayables.css";

interface PendingBill {
    bill_id: number;
    bill_no: string;
    bill_date: string;
    amount: number;
    paid: number;
    balance: number;
}

interface Lab {
    lab_id: number;
    lab_name: string;
    contact: string;
    total_billed: number;
    total_paid: number;
    balance_due: number;
    pending_bills: PendingBill[];
}

interface LabPayablesResponse {
    labs: Lab[];
}

export default function LabPayables() {
    const { showToast } = useToast();
    const [data, setData] = useState<LabPayablesResponse | null>(null);
    const [expandedLab, setExpandedLab] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    
    // Modal State
    const [paymentModal, setPaymentModal] = useState<{ lab: Lab; bill: PendingBill } | null>(null);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [receiptNo, setReceiptNo] = useState("");
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { loadLabPayables(); }, []);

    const loadLabPayables = async () => {
        setLoading(true);
        try {
            const response = await apiGet("/api/manager/labs/payables");
            setData(response as LabPayablesResponse);
        } catch (e) {
            showToast({ message: "Failed to load lab payables.", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentSubmit = async () => {
        if (!paymentModal || !paymentAmount || Number(paymentAmount) <= 0) {
            showToast({ message: "Enter a valid payment amount.", type: "warning" });
            return;
        }
        
        if (Number(paymentAmount) > paymentModal.bill.balance) {
            const confirm = window.confirm(
                `Payment amount (₹${paymentAmount}) exceeds balance (₹${paymentModal.bill.balance}). Continue?`
            );
            if (!confirm) return;
        }

        setSubmitting(true);
        try {
            await apiPost("/api/manager/lab-payments", {
                bill_id: paymentModal.bill.bill_id,
                amount: Number(paymentAmount),
                receipt_no: receiptNo || null,
                paid_date: paymentDate
            });
            showToast({ message: "Lab payment recorded successfully.", type: "success" });
            
            // Emit Finance Event for Lab Payment (Expenditure)
            if (Number(paymentAmount) > 0) {
                await emitFinanceEvent({
                    source: "LAB_PAYMENT",
                    ref_id: String(paymentModal.bill.bill_id),
                    debit: 0,
                    credit: Number(paymentAmount),
                    description: `Lab payment to ${paymentModal.lab.lab_name} for bill #${paymentModal.bill.bill_no}`,
                    meta: {
                        lab_id: paymentModal.lab.lab_id,
                        bill_id: paymentModal.bill.bill_id,
                        receipt_no: receiptNo
                    }
                });
            }
            setPaymentModal(null);
            setPaymentAmount("");
            setReceiptNo("");
            loadLabPayables();
        } catch (e) {
            showToast({ message: "Failed to record lab payment.", type: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const inputStyle = { padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', width: '100%', boxSizing: 'border-box' as const };

    return (
        <div className="mod-standalone-window" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="mod-window-header">
                <div>
                    <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>🧪 Lab Payables</h2>
                    <p className="mod-text-muted">Manage pending vendor bills and record payments.</p>
                </div>
                <Button variant="outline" onClick={loadLabPayables} isLoading={loading}>Refresh List</Button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading lab payables...</div>
                ) : data && data.labs.length > 0 ? (
                    data.labs.map((lab) => (
                        <div key={lab.lab_id} style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: 'var(--mod-shadow-sm)' }}>
                            {/* Lab Summary Header */}
                            <div 
                                style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: expandedLab === lab.lab_id ? '#f8fafc' : '#fff', transition: 'background 0.2s' }}
                                onClick={() => setExpandedLab(expandedLab === lab.lab_id ? null : lab.lab_id)}
                            >
                                <div>
                                    <h3 style={{ margin: '0 0 4px 0', fontSize: '1.2rem', color: '#0f172a' }}>{lab.lab_name}</h3>
                                    {lab.contact && <div style={{ fontSize: '0.9rem', color: '#64748b' }}>📞 {lab.contact}</div>}
                                </div>
                                <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Total Billed</div>
                                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#334155' }}>₹{lab.total_billed.toFixed(2)}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#166534', textTransform: 'uppercase', fontWeight: 600 }}>Paid</div>
                                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d' }}>₹{lab.total_paid.toFixed(2)}</div>
                                    </div>
                                    <div style={{ textAlign: 'right', background: '#fef2f2', padding: '8px 16px', borderRadius: '8px', border: '1px solid #fecaca' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#991b1b', textTransform: 'uppercase', fontWeight: 700 }}>Balance Due</div>
                                        <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#dc2626' }}>₹{lab.balance_due.toFixed(2)}</div>
                                    </div>
                                    <span style={{ fontSize: '1.2rem', color: '#94a3b8', transform: expandedLab === lab.lab_id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                                        ▼
                                    </span>
                                </div>
                            </div>

                            {/* Expanded Pending Bills */}
                            {expandedLab === lab.lab_id && (
                                <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                    <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#334155' }}>Pending Bills ({lab.pending_bills.length})</h4>
                                    
                                    {lab.pending_bills.length === 0 ? (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#16a34a', background: '#f0fdf4', borderRadius: '8px', border: '1px dashed #bbf7d0' }}>
                                            🎉 All bills have been paid for this lab!
                                        </div>
                                    ) : (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left', background: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                            <thead style={{ background: '#f1f5f9', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                                                <tr>
                                                    <th style={{ padding: '12px 16px' }}>Bill No</th>
                                                    <th style={{ padding: '12px 16px' }}>Bill Date</th>
                                                    <th style={{ padding: '12px 16px', textAlign: 'right' }}>Amount</th>
                                                    <th style={{ padding: '12px 16px', textAlign: 'right' }}>Paid</th>
                                                    <th style={{ padding: '12px 16px', textAlign: 'right' }}>Balance</th>
                                                    <th style={{ padding: '12px 16px', textAlign: 'right' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lab.pending_bills.map((bill) => (
                                                    <tr key={bill.bill_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '12px 16px', fontWeight: 600 }}>{bill.bill_no}</td>
                                                        <td style={{ padding: '12px 16px', color: '#64748b' }}>{bill.bill_date}</td>
                                                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>₹{bill.amount.toFixed(2)}</td>
                                                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#16a34a' }}>₹{bill.paid.toFixed(2)}</td>
                                                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>₹{bill.balance.toFixed(2)}</td>
                                                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                            <Button 
                                                                variant="primary" 
                                                                size="sm" 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setPaymentModal({ lab, bill });
                                                                    setPaymentAmount(bill.balance.toString());
                                                                    setReceiptNo("");
                                                                    setPaymentDate(new Date().toISOString().split("T")[0]);
                                                                }}
                                                            >
                                                                Pay
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="mod-empty-state" style={{ height: '300px', border: '1px dashed #cbd5e1' }}>
                        <span style={{ fontSize: '2.5rem' }}>🧪</span>
                        <p style={{ marginTop: '12px' }}>No labs with pending bills found.</p>
                    </div>
                )}
            </div>

            {/* Modern Blurred Payment Modal */}
            {paymentModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setPaymentModal(null)}>
                    <div style={{ width: '400px', background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem' }}>Pay Lab Bill</h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: '#64748b' }}>
                            {paymentModal.lab.lab_name} • Bill #{paymentModal.bill.bill_no}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Amount to Pay (₹)</label>
                                <input
                                    type="number"
                                    value={paymentAmount}
                                    placeholder={`Balance: ₹${paymentModal.bill.balance}`}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    style={inputStyle}
                                    autoFocus
                                />
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                                    Current Balance: <strong style={{ color: '#dc2626' }}>₹{paymentModal.bill.balance.toFixed(2)}</strong>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Receipt No (Optional)</label>
                                <input type="text" value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)} style={inputStyle} />
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Payment Date</label>
                                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} max={new Date().toISOString().split("T")[0]} style={inputStyle} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <Button variant="ghost" onClick={() => setPaymentModal(null)}>Cancel</Button>
                            <Button variant="primary" onClick={handlePaymentSubmit} isLoading={submitting}>Record Payment</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
